import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';

// Fan-out policy as SERVED by the backend (`GET /api/*-mailings/clients` →
// `policy`, `min_send_time`, `deferred_heatup_clients`), mirroring
// MailingSharedService.MAILING_POLICY. Until 2026-09-10 this file carried a
// hand-copied cap map; it drifted by design — now the dialog renders the same
// table the fan-out enforces. Missing policy (older API) = nothing is marked.
export interface MailingPolicyRule {
  caps: Record<string, number>;
  blocked: string[];
}
export interface MailingPolicyTable {
  webinar: { heatup: MailingPolicyRule; redlink: MailingPolicyRule };
  plx: MailingPolicyRule;
}
export type MailingSystem = 'webinar' | 'plx';

/** 1-based position among emails of the same kind (heat-ups among heat-ups). */
export const emailOrdinal = (
  emails: ReadonlyArray<{ mailing_number: number; mailing_type?: string | null }>,
  mailingNumber: number,
): number => {
  const target = emails.find((e) => e.mailing_number === mailingNumber);
  const kind = target?.mailing_type === 'redlink' ? 'redlink' : 'heatup';
  const same = emails
    .filter((e) => (e.mailing_type === 'redlink' ? 'redlink' : 'heatup') === kind)
    .map((e) => e.mailing_number)
    .sort((a, b) => a - b);
  const idx = same.indexOf(mailingNumber);
  return idx === -1 ? mailingNumber : idx + 1;
};

export const resolvePolicyForChip = (
  policy: MailingPolicyTable | null | undefined,
  system: MailingSystem,
  mailingType: string | null | undefined,
  ordinal: number,
  clientCode: string,
): { allowed: boolean; reason: 'blocked' | 'cap' | null; cap: number | null } => {
  if (!policy) return { allowed: true, reason: null, cap: null };
  const rule =
    system === 'plx'
      ? policy.plx
      : mailingType === 'redlink'
        ? policy.webinar.redlink
        : policy.webinar.heatup;
  const code = clientCode.toLowerCase();
  if (rule.blocked.includes(code)) return { allowed: false, reason: 'blocked', cap: null };
  const cap = rule.caps[code];
  if (cap !== undefined && ordinal > cap) return { allowed: false, reason: 'cap', cap };
  return { allowed: true, reason: null, cap: cap ?? null };
};

export interface ScheduleEmail {
  mailing_number: number;
  mailing_type?: string | null;
  subject: string;
  scheduled_datetime: string;
  selected_clients: string[];
  from_name: string;
  use_client_from_names: boolean;
  client_from_names: Record<string, string> | null;
  schedule_statuses?: string | null;
  status?: string;
}

export interface ScheduleClient {
  code: string;
  name: string;
  esp_type: string;
}

export interface SchedulabilityResult {
  schedulable: boolean;
  reason?: string;
}

export interface ScheduleConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  emails: ScheduleEmail[];
  clients: ScheduleClient[];
  campaignName: string;

  // Customizable scheduling rules
  isSchedulable: (email: ScheduleEmail) => SchedulabilityResult;
  formatScheduledTime: (email: ScheduleEmail) => string;

  // Callbacks
  onSchedule: (emailNumbers: number[]) => Promise<void>;
  onSuccess?: () => void;

  // Optional
  subtitle?: string;
  showIterableWarning?: boolean;

  // Fan-out policy (from the clients endpoint). `system` picks the rule set.
  system?: MailingSystem;
  policy?: MailingPolicyTable | null;
  /** Per-client earliest send time (Berlin, 'HH:mm:ss'), e.g. { dnv: '09:00:00' }. */
  minSendTime?: Record<string, string> | null;
  /** Clients whose heat-ups are created by the morning worker (waiting rows). */
  deferredHeatupClients?: string[] | null;
}

type SchedulingStatus = 'idle' | 'loading' | 'success' | 'error';

const ScheduleConfirmationDialog: React.FC<ScheduleConfirmationDialogProps> = ({
  open,
  onClose,
  emails,
  clients,
  campaignName,
  isSchedulable,
  formatScheduledTime,
  onSchedule,
  onSuccess,
  subtitle,
  showIterableWarning = true,
  system = 'webinar',
  policy = null,
  minSendTime = null,
  deferredHeatupClients = null,
}) => {
  const [selectedEmails, setSelectedEmails] = useState<number[]>([]);
  const [status, setStatus] = useState<SchedulingStatus>('idle');
  const [message, setMessage] = useState('');
  const prevOpenRef = React.useRef(false);

  // Calculate schedulable emails and pre-select them when dialog opens
  // Only reset state when dialog transitions from closed to open
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only initialize when dialog opens (not on every render while open)
    if (open && !wasOpen) {
      const schedulable = emails
        .filter((email) => isSchedulable(email).schedulable)
        .map((email) => email.mailing_number);
      setSelectedEmails(schedulable);
      setStatus('idle');
      setMessage('');
    }
  }, [open, emails, isSchedulable]);

  const handleClose = () => {
    setStatus('idle');
    setMessage('');
    onClose();
  };

  const handleConfirm = async () => {
    if (selectedEmails.length === 0) return;

    setStatus('loading');
    setMessage('');

    try {
      await onSchedule(selectedEmails);
      setStatus('success');
      setMessage('Erfolgreich geplant');
      onSuccess?.();
    } catch (err) {
      setStatus('error');
      setMessage((err as Error).message);
    }
  };

  const hasIterableEmails = emails.some((e) =>
    e.selected_clients?.some((code) => {
      const client = clients.find((c) => c.code === code);
      return client?.esp_type === 'iterable';
    }),
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ScheduleIcon color="success" />
          Kampagne planen?
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Die Kampagne wird mit folgenden Einstellungen geplant:
        </Typography>

        <List dense>
          <ListItem>
            <ListItemText primary="Kampagne" secondary={campaignName} />
          </ListItem>

          {subtitle && (
            <ListItem>
              <ListItemText primary="Details" secondary={subtitle} />
            </ListItem>
          )}

          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText
              primary="E-Mails auswählen"
              secondary="Nur ausgewählte E-Mails werden geplant"
            />
            <Box sx={{ width: '100%', mt: 1 }}>
              {emails.map((email) => {
                const result = isSchedulable(email);
                const isSelected = selectedEmails.includes(email.mailing_number);

                // Status text and color
                let statusText = formatScheduledTime(email);
                let statusColor: 'error' | 'text.secondary' | 'warning' = 'text.secondary';

                if (!result.schedulable && result.reason) {
                  statusText = `(${result.reason})`;
                  statusColor =
                    result.reason.includes('geplant') || result.reason.includes('gesendet')
                      ? 'warning'
                      : 'error';
                }

                return (
                  <FormControlLabel
                    key={email.mailing_number}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      ml: 0,
                      mb: 0.5,
                      opacity: result.schedulable ? 1 : 0.5,
                      '& .MuiFormControlLabel-label': { flex: 1 },
                    }}
                    control={
                      <Checkbox
                        checked={isSelected}
                        disabled={!result.schedulable}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmails((prev) => [...prev, email.mailing_number]);
                          } else {
                            setSelectedEmails((prev) =>
                              prev.filter((n) => n !== email.mailing_number),
                            );
                          }
                        }}
                      />
                    }
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Typography variant="body2">
                          E-Mail {email.mailing_number}:{' '}
                          <em>
                            {email.subject?.substring(0, 30)}
                            {(email.subject?.length || 0) > 30 ? '...' : ''}
                          </em>
                        </Typography>
                        <Typography variant="body2" color={statusColor} sx={{ ml: 2 }}>
                          {statusText}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Ziel-Clients und Absender pro E-Mail"
              secondary={
                <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0, listStyleType: 'none' }}>
                  {emails.map((email) => {
                    const emailClients = email.selected_clients || [];
                    return (
                      <li key={email.mailing_number} style={{ marginBottom: 8 }}>
                        <Typography variant="body2" color="text.secondary" component="div">
                          <strong>E-Mail {email.mailing_number}:</strong>
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {emailClients.length === 0 ? (
                            <Typography variant="caption" color="error">
                              Keine Clients ausgewählt
                            </Typography>
                          ) : (
                            emailClients.map((code) => {
                              const client = clients.find((c) => c.code === code);
                              const clientFromNames = email.client_from_names || {};
                              const fromName = email.use_client_from_names
                                ? clientFromNames[code] || email.from_name
                                : email.from_name;
                              const decision = resolvePolicyForChip(
                                policy,
                                system,
                                email.mailing_type,
                                emailOrdinal(emails, email.mailing_number),
                                code,
                              );
                              const capped = !decision.allowed;
                              const isDeferred =
                                system === 'webinar' &&
                                email.mailing_type !== 'redlink' &&
                                (deferredHeatupClients ?? []).includes(code.toLowerCase());
                              const floor = minSendTime?.[code.toLowerCase()];
                              const deferredNote = isDeferred
                                ? ` · ab ${(floor ?? '09:00:00').slice(0, 5)} · Morning-Worker`
                                : '';
                              return (
                                <Chip
                                  key={code}
                                  size="small"
                                  label={
                                    capped
                                      ? decision.reason === 'cap'
                                        ? `${client?.name || code}: entfällt (max. ${decision.cap} Mails)`
                                        : `${client?.name || code}: entfällt (Policy)`
                                      : `${client?.name || code}: ${fromName}${deferredNote}`
                                  }
                                  sx={
                                    capped
                                      ? { fontSize: '0.75rem', textDecoration: 'line-through' }
                                      : {
                                          backgroundColor:
                                            client?.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                                          color: 'white',
                                          fontSize: '0.75rem',
                                        }
                                  }
                                />
                              );
                            })
                          )}
                        </Box>
                      </li>
                    );
                  })}
                </Box>
              }
            />
          </ListItem>
        </List>

        {showIterableWarning && hasIterableEmails && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
            <strong>Achtung:</strong> Iterable-Kampagnen können nach dem Senden nicht mehr über die
            API storniert werden. Bei Bedarf muss die Stornierung direkt in Iterable erfolgen.
          </Alert>
        )}

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Die E-Mails werden zu den angezeigten Zeitpunkten automatisch vom ESP versendet.
        </Typography>

        {status === 'loading' && (
          <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2 }}>
            <CircularProgress size={24} />
            <Typography>Kampagne wird geplant...</Typography>
          </Box>
        )}

        {status === 'success' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        {status === 'error' && (
          // pre-line: the backend returns one bullet per blocked (E-Mail, Client) pair,
          // newline-separated. Without this they collapse into an unreadable run-on line.
          <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            {message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{status === 'idle' ? 'Abbrechen' : 'Schließen'}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          startIcon={
            status === 'loading' ? <CircularProgress size={20} color="inherit" /> : <ScheduleIcon />
          }
          disabled={selectedEmails.length === 0 || status !== 'idle'}
        >
          {status === 'loading'
            ? 'Wird geplant...'
            : selectedEmails.length > 0
              ? `${selectedEmails.length} E-Mail(s) planen`
              : 'Keine E-Mails ausgewählt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleConfirmationDialog;
